import { useMutation, useQuery } from "@tanstack/react-query";
import { createBranchTenant, createTenant, deactivateTenant, fetchAllTenants, fetchTenantBranding, fetchTenantById, updateTenant } from "../api/tenantService";
import { queryClient } from "../queryClient";
import { toast } from "react-toastify";
import { CreateBranchPayload, CreateTenantPayload, Tenant, UpdateTenantPayload } from "../../types/tenant";
import { AxiosError } from "axios";

// El back explica en español los rechazos que tienen arreglo (por ejemplo, un
// 413 por imagen demasiado pesada); el texto genérico queda de respaldo.
const mensajeDelServidor = (error: unknown, respaldo: string) =>
    (error as AxiosError<{ message?: string }>)?.response?.data?.message || respaldo;

export const useAllTenants = () => useQuery({
    queryKey: ['tenants', 'all'],
    queryFn: () => fetchAllTenants()
});

export const useTenantDetailQuery = (tenantId?: string) => useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => fetchTenantById(tenantId!),
    enabled: !!tenantId 
});

export const useCreateTenantMutation = () => useMutation({
    mutationFn: createTenant,
    onSuccess:() => {
        queryClient.invalidateQueries({ queryKey: ['tenants', 'all'] });
        toast.success('Tenant creado exitosamente');
    },
    onError: (error) => {
        toast.error(mensajeDelServidor(error, 'Error al crear el tenant'));
    },  
});

export const useCreateBranchMutation = () => useMutation({
    mutationFn: ({ tenantId, data }: { tenantId: string, data: CreateBranchPayload }) => createBranchTenant(tenantId, data),
    onSuccess:() => {
        queryClient.invalidateQueries({ queryKey: ['tenants', 'all'] });
        toast.success('Sucursal creada exitosamente');
    },
    onError: (error) => {
        toast.error(mensajeDelServidor(error, 'Error al crear la sucursal'));
    },
});

export const useUpdateTenantMutation = () => useMutation({
    mutationFn: ({id, data}: {id: string, data: UpdateTenantPayload}) => updateTenant(id, data),
    onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['tenants', 'all'] });
        queryClient.invalidateQueries({ queryKey: ['tenant', variables.id] });
        toast.success('Tenant actualizado exitosamente');
    },
    onError: (error) => {
        toast.error(mensajeDelServidor(error, 'Error al actualizar el tenant'));
    },  
});

export const useDeactivateTenantMutation = () => useMutation({
    mutationFn: (tenantId: string) => deactivateTenant(tenantId),
    onSuccess: (_data, tenantId) => {
        queryClient.invalidateQueries({ queryKey: ['tenants', 'all'] });
        queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
        toast.success('Tenant desactivado exitosamente');
    },
    onError: () => {
        toast.error('Error al desactivar el tenant');
    },  
});

export const useTenantBranding = (code: string) => useQuery({
    queryKey: ['tenant-branding', code],
    queryFn: () => fetchTenantBranding(code),
    staleTime: 5 * 60 * 1000,
    enabled: !!code,
    retry: false,
});